import { useEffect, useMemo, useState } from 'react'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { CustomTable } from '../../../../components/customTable'
import { PagePreloader } from '../../../../components/pagePreloader'
import { SmoothTransition } from '../../../../components/pageTransition'
import { RenderDefaultActions, RenderStatus } from '../../common'
import { usePopup } from '../../../../context/popupContext'
import { useNotificationBar } from '../../../../context/notificationContext'
import { getErrorResponse, getUpdatedSearchParams, parseSearchParams } from '../../../../services/util'
import { useDeleteTool, useGetTools, useStatusTool } from 'hooks/tools'
import { useGetAppVersions } from 'hooks/app-version'
import { useSearchParams } from 'react-router-dom'
import { PlusIcon } from '@heroicons/react/24/outline'
import AppVersionModal from './AppVersionModal'

const AppVersionMaster = () => {
    const popup = usePopup()
    const notification = useNotificationBar()
    const rowSelectionState = useState<RowSelectionState>({})

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editData, setEditData] = useState<any>(null)

    const { mutate: deleteTool } = useDeleteTool()
    const { mutate: toolStatus } = useStatusTool()
    // Filter Params
    const [searchParams, setSearchParams] = useSearchParams()
    const filterParams: any = parseSearchParams(searchParams, 'filter')

    const {
      data,
      isLoading,
      refetch,
      isError,
  } = useGetAppVersions(filterParams, {
      keepPreviousData: true,
  })

  const useFormHook = useForm({
    defaultValues: filterParams,
})

    const normalizedData = useMemo(() => {
        const list = Array.isArray(data?.data?.results) ? data.data.results : []

        const results = list.map((item: any) => {
            const rawStatus = item?.status ?? item?.isActive ?? item?.is_active
            const status = rawStatus === true || rawStatus === 1 || rawStatus === '1'
            const isLatest = item?.is_latest === 1 || item?.is_latest === true

            return {
                ...item,
                id: item?.toolId ?? item?.id ?? item?._id,
                appVersion: item?.version || item?.appVersion || '-',
                platform: item?.platform || '-',
                description: item?.description || '-',
                isLatest: isLatest,
                releaseDate: item?.createdAt || item?.updatedAt || null,
                status,
            }
        })

        return {
            pages: data?.data?.pages || 0,
            total: data?.data?.total || results.length || 0,
            results,
        }
    }, [data])

    // -------------------------------
    //  OPEN ADD/UPDATE DIALOG
    // -------------------------------
    const openAddUpdateDialog = (row: any, type: 'edit' | 'add') => {
        if (type === 'edit' && row) {
            setEditData({ ...row })
            setIsModalOpen(true)
        }
        if (type === 'add') {
            setEditData(null)
            setIsModalOpen(true)
        }
    }

    // -------------------------------
    //  CLOSE MODAL
    // -------------------------------
    const closeModal = () => {
        setIsModalOpen(false)
        setEditData(null)
    }

    // -------------------------------
    //  ON MODAL SUCCESS
    // -------------------------------
    const onModalSuccess = () => {
        refetch()
    }

    const onAction = (actionType: string, row: any) => {
        if (actionType === 'edit') {
            openAddUpdateDialog(row, 'edit')
        }
    }

    const columns: ColumnDef<any>[] = [
        {
            id: 'sr_no',
            header: 'ID',
            cell: ({ row, table }) => {
                const pageIndex = table.getState().pagination.pageIndex
                const pageSize = table.getState().pagination.pageSize
                return pageIndex * pageSize + row.index + 1
            },
            enableSorting: false,
        },
        {
            accessorKey: 'appVersion',
            header: 'APP VERSION',
            cell: ({ getValue }) => getValue() || '-',
        },
        {
            accessorKey: 'platform',
            header: 'PLATFORM',
           cell: ({ getValue }) => getValue() || '-',
              
        },
        {
            accessorKey: 'description',
            header: 'DESCRIPTION',
            cell: ({ getValue }) => {
                const description = getValue() as string
                if (!description || description === '-') return '-'
                
                const maxLength = 50
                const truncated = description.length > maxLength 
                    ? description.substring(0, maxLength) + '...' 
                    : description
                
                return (
                    <div title={description} className="truncate">
                        {truncated}
                    </div>
                )
            },
        },
        {
            id: 'isLatest',
            header: 'LATEST',
            accessorFn: (row) => row.isLatest,
            cell: ({ row }) => {
                const isLatest = row.original.isLatest
                return isLatest ? 'Yes' : 'No'
            },
        },
                {
            accessorKey: 'releaseDate',
            header: 'RELEASE DATE',
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null
                return value ? moment(value).format('DD MMM YYYY') : '-'
            },
        },
        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) =>
                RenderDefaultActions(row, ['edit'], onAction),
        },
    ]

    // Handle form filter changes
    useEffect(() => {
      const subscriber = useFormHook.watch((values, { name }) => {
          if (!name) return

          const updated = getUpdatedSearchParams(filterParams, values, name)

          if (Object.keys(updated)?.length) {
              const sp = encodeURIComponent(JSON.stringify(updated))
              setSearchParams({ filter: sp })
          } else {
              setSearchParams({})
          }
      })

      return () => subscriber.unsubscribe()
  }, [useFormHook, filterParams, setSearchParams])

    return (
        <div className="w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md">
            <SmoothTransition>
                {isLoading ? (
                    <PagePreloader />
                ) : isError ? (
                    <div className="text-center py-12">
                        <p className="text-red-500">Failed to load app versions. Please try again.</p>
                    </div>
                ) : (
                    <CustomTable
                        title="App Version"
                        titleLabel={`Total ${normalizedData?.total || 0} records`}
                        columns={columns}
                        data={normalizedData.results}
                        pages={normalizedData.pages}
                        totalRecords={normalizedData.total}
                        isLoading={isLoading}
                        isErrored={isError}
                        formHook={useFormHook}
                        rowSelectionState={rowSelectionState}
                        onRefresh={refetch}
        onAddAction={() => openAddUpdateDialog(null, 'add')}
                    />
                )}
            </SmoothTransition>
            {/* App Version Modal */}
            <AppVersionModal
                isOpen={isModalOpen}
                onClose={closeModal}
                editData={editData}
                onSuccess={onModalSuccess}
            />
        </div>
    )
}

export default AppVersionMaster